import { computed, type MaybeRef, unref } from 'vue';
import { isNil } from 'lodash-es';
import type { Payments } from '@aviata/platform-types';
import { useQuery } from '@tanstack/vue-query';

import { sentryModel } from '@/shared/model';

import { createBill, getBillById, getBillOptions, searchBills } from '../api';

export function useBillOptions({
  billId,
  enabled,
}: {
  billId: MaybeRef<string | undefined>;
  enabled?: MaybeRef<boolean>;
}) {
  const billOptionsQuery = useQuery({
    queryKey: ['payments', 'bills', billId, 'options'],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => {
      const _billId = unref(billId);

      if (isNil(_billId)) {
        return Promise.reject(new Error('ERR_BILL_ID_IS_NIL'));
      }

      return getBillOptions({ billId: _billId });
    },
    enabled: computed(() => {
      const _enabled = unref(enabled);
      return !isNil(unref(billId)) && isNil(_enabled) ? true : _enabled;
    }),
    refetchOnWindowFocus: false,
  });

  const options = computed(() => billOptionsQuery.data.value);

  const isLoading = computed(() => billOptionsQuery.isLoading.value);

  function refetch() {
    return billOptionsQuery.refetch();
  }

  return {
    options,
    isLoading,
    refetch,
  };
}

function getMainBill(bills: Payments.Bill.Entity[]) {
  return Array.isArray(bills)
    ? bills.find(bill => bill.type === 'MAIN')
    : undefined;
}

async function getBill({ orderId }: { orderId: string }) {
  try {
    const data = await searchBills({ orderId });
    const mainBill = data ? getMainBill(data) : undefined;

    if (isNil(mainBill)) {
      return await createBill({ orderId });
    }

    return mainBill;
  } catch (error) {
    sentryModel.logBaseError({
      error: error as Error,
      description: "Payment error: couldn't retrieve bill",
    });
  }
}

function _useBill({
  orderId,
  billId,
}: {
  orderId?: MaybeRef<string | undefined>;
  billId?: MaybeRef<string | undefined>;
}) {
  const billQuery = useQuery({
    queryKey: computed(() => {
      const _orderId = unref(orderId);
      const _billId = unref(billId);

      if (!isNil(_billId)) {
        return ['payments', 'bill', 'by-id', _billId];
      }
      if (!isNil(_orderId)) {
        return ['payments', 'bill', 'by-order', _orderId];
      }
      return ['payments', 'bill', 'empty'];
    }),
    queryFn: () => {
      const _orderId = unref(orderId);
      const _billId = unref(billId);

      if (!isNil(_billId)) {
        return getBillById({ billId: _billId });
      }
      if (!isNil(_orderId)) {
        return getBill({ orderId: _orderId });
      }
      return Promise.resolve({} as Payments.Bill.Entity);
    },
    enabled: computed(() => {
      const _orderId = unref(orderId);
      const _billId = unref(billId);
      return !isNil(_orderId) || !isNil(_billId);
    }),
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  function refetch() {
    return billQuery.refetch();
  }

  const bill = computed(() => billQuery.data.value || null);
  const error = computed(() => billQuery.error.value);

  const isLoading = computed(() => billQuery.isLoading.value);
  const isRefetching = computed(() => billQuery.isRefetching.value);
  const isError = computed(() => billQuery.isError.value);

  const isPaid = computed(() => bill.value?.status === 'PAID');
  const isExpired = computed(() => bill.value?.status === 'EXPIRED');

  return {
    bill,
    error,
    isLoading,
    isRefetching,
    isError,
    isPaid,
    isExpired,
    refetch,
  };
}

// Use for payment page when orderId is available
export function useBill(params: {
  orderId: MaybeRef<string | undefined>;
  billId?: never;
}): ReturnType<typeof _useBill>;

// Use for surcharge when orderId is unknown
export function useBill(params: {
  billId: MaybeRef<string | undefined>;
  orderId?: never;
}): ReturnType<typeof _useBill>;

export function useBill({
  orderId,
  billId,
}: {
  orderId?: MaybeRef<string | undefined>;
  billId?: MaybeRef<string | undefined>;
}) {
  return _useBill({ orderId, billId });
}
